import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  useTicketDetailsAfterSelectingDispositionWithMultiLevelQuery,
  useUpdateTicketWorkflowMutation,
  useTicketDynamicFieldsQuery,
  useTicketDynamicFieldsByDispositionQuery,
} from "../../Services/ticket/CreateTicket";
import { useReplyMailMutation } from "../../Services/email/EmailInbox";
import { useInternalNotesMutation } from "../../Services/Messages/Messages";
import {
  InputLabel,
  ListItemText,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  Box,
  Button,
  Chip,
} from "@mui/material";
import { PhoneInput } from "react-international-phone";
import { capitalizeFirstLetter } from "../reusableComponent/CapitalLetter";
import { LazyMultiLevelDropdown } from "../LazyMultiLevelDropdown";
import LoadingSpinner from "../LoadingSpinner";
import { toast } from "react-toastify";
import { Loader } from "react-bootstrap-typeahead";
import { getMultiLevelDisplayValue, getMultiLevelTicketPath } from "../../utils/multiLevelFieldValue";

function getFullPathForDropdown(
  field: any,
  lastValue: string | null | undefined
): string | null {
  if (!lastValue || !field?.multiple_level) return lastValue || null;

  const stack: { node: any; path: string[] }[] = field.multiple_level.map(
    (node: any) => ({ node, path: [node.choice] })
  );

  while (stack.length > 0) {
    const { node, path } = stack.pop()!;
    if (node.choice === lastValue) {
      return path.join(">");
    }
    Object.keys(node).forEach((key) => {
      if (key.startsWith("level") && Array.isArray(node[key])) {
        node[key].forEach((child: any) => {
          stack.push({ node: child, path: [...path, child.choice] });
        });
      }
    });
  }

  return lastValue;
}

interface IPROPS {
  closeModal: any;
  ticketId: any;
  lastSelectedId: any;
  ticketName: any;
  fetchDispositionStatus: any;
  refetch: any;
  otherMailDetails: any;
  cc: any;
  bcc: any;
  emails: any;
  editorHtml: any;
  files: any;
  templateAttachment: any;
  setCheckingForReplyApi: any;
  setLiftingReplyLoader: any;
  setShowReplyCard: any;
}

interface ErrorState {
  [key: string]: string;
}

const EMPTY_DYNAMIC_FIELDS: any[] = [];

const WorkflowModal: React.FC<IPROPS> = ({
  closeModal,
  ticketId,
  ticketName,
  fetchDispositionStatus,
  refetch,
  otherMailDetails,
  bcc,
  cc,
  emails,
  editorHtml,
  files,
  templateAttachment,
  setCheckingForReplyApi,
  setLiftingReplyLoader,
  setShowReplyCard,
}) => {
  const [internalNoteText, setInternalNoteText] = useState("");
  const [internalNoteFiles, setInternalNoteFiles] = useState<any[]>([]);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [workFlowLoader, setWorkflowLoader] = useState(false);
  const [replyLoader, setReplyLoader] = useState(false);
  const [dynamicFieldsLoader, setDynamicFieldsLoader] = useState(false);

  const [dynamicFields, setDynamicFields] = useState<any>("");
  const [errors, setErrors] = useState<ErrorState>({});

  const [originalFields, setOriginalFields] = useState({}); // State to track original values
  const { data: tickets } =
    useTicketDetailsAfterSelectingDispositionWithMultiLevelQuery(ticketId);
  const { data: ticketDynamicFieldsByDisposition, status: dynamicFieldsStatus } =
    useTicketDynamicFieldsByDispositionQuery(ticketName, {
      skip: !ticketName,
    });
  const dynamicData = useMemo(() => {
    if (!ticketName || !ticketDynamicFieldsByDisposition) {
      return EMPTY_DYNAMIC_FIELDS;
    }

    return Array.isArray(ticketDynamicFieldsByDisposition)
      ? ticketDynamicFieldsByDisposition
      : ticketDynamicFieldsByDisposition?.results ?? EMPTY_DYNAMIC_FIELDS;
  }, [ticketName, ticketDynamicFieldsByDisposition]);

  // Changed from defaultSelect to currentStatus to better track the actual status
  const [currentStatus, setCurrentStatus] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedAllStatus, setAllSelectedStatus] = useState<any>("");

  const [replyMail, { status: replyStatus }] = useReplyMailMutation();

  const [trigger, { isLoading, status: ticketWorkflow }] =
    useUpdateTicketWorkflowMutation();
  const [saveInternalNote, { isLoading: isSavingInternalNote }] =
    useInternalNotesMutation();

  useEffect(() => {
    if (replyStatus === "pending") {
      setReplyLoader(true);
    } else {
      setReplyLoader(false);
    }
  }, [replyStatus]);

  useEffect(() => {
    if (ticketWorkflow === "pending") {
      setWorkflowLoader(true);
    } else {
      setWorkflowLoader(false);
    }
  }, [ticketWorkflow]);

  useEffect(() => {
    if (dynamicFieldsStatus === "pending") {
      setDynamicFieldsLoader(true);
    } else {
      setDynamicFieldsLoader(false);
    }
  }, [dynamicFieldsStatus]);

  useEffect(() => {
    if (tickets) {
      setCurrentStatus(tickets?.status_display || "");
      // When ticket data loads, initialize the selected status with the current status
      if (tickets?.status_display) {
        const matchingStatus = fetchDispositionStatus?.all_statuses?.find(
          (status) =>
            status.status === tickets.status_display ||
            (status.status.status &&
              status.status.status === tickets.status_display)
        );

        if (matchingStatus) {
          const formattedStatus = formatStatusData(matchingStatus);
          setSelectedStatus(formattedStatus.status_type);
          setAllSelectedStatus(formattedStatus);
        }
      }
    }
  }, [tickets, fetchDispositionStatus]);

  const handleInputChange = useCallback((label, value) => {
    setIsButtonDisabled(true);
    // Clear error only for the field being changed
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors[label];
      return newErrors;
    });
    // Update dynamicFields and track original field values
    setDynamicFields((prevState) => ({
      ...prevState,
      [label]: value,
    }));
  }, []);

  useEffect(() => {
    if (tickets) {
      const dynamicFieldsArray = Object.entries(
        tickets.dynamic_fields || {}
      ).reduce((acc, [label, value]) => ({ ...acc, [label]: value }), {});

      const fetchingStatusValue = tickets?.status_display
        ? tickets?.status_display
        : "";
      setDynamicFields(dynamicFieldsArray);
      setOriginalFields(dynamicFieldsArray); // Set original fields for comparison
    }
  }, [tickets]);

  const validateForm = () => {
    const newErrors: ErrorState = {};

    if (!internalNoteText.trim()) {
      newErrors.internal_notes = "Internal notes is required";
    }

    dynamicData.forEach((field: any) => {
      const fieldValue = dynamicFields[field.label];

      if (field.is_required && !fieldValue) {
        newErrors[field.label] = `${field.label} is required`;
      }

      // Example: Add phone number validation logic here
      if (field.is_required && field.field_type === "tel-phone" && fieldValue) {
        // If you have specific phone validation, you could add it here
        const isValidPhone =
          /^(?:\+?\d{1,3})?[ -]?\(?\d{1,4}?\)?[ -]?\d{1,4}[ -]?\d{1,4}(?: x\d{1,4})?$/.test(
            fieldValue
          );
        if (!isValidPhone) {
          newErrors[
            field.label
          ] = `${field.label} must be a valid phone number`;
        }
      }

      if (field.field_type === "text-area" && field.label === "remarks") {
        if (!fieldValue) {
          newErrors[field.label] = `${field.label} is required`;
        }
      }
    });

    // Only check status if user has interacted with the form
    if (isButtonDisabled && (!selectedStatus || !selectedAllStatus)) {
      newErrors.status_type = "Status is required";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  /** Normalize date values so unchanged dates compare equal (avoids re-sending on timezone/format drift). */
  const normalizeDateForCompare = (val: unknown): string => {
    if (val === null || val === undefined || val === "") return "";
    const d = new Date(val as string | number | Date);
    if (Number.isNaN(d.getTime())) return String(val);
    return d.toISOString().replace("Z", "+00:00");
  };

  const hasDynamicFieldChanged = (
    key: string,
    field: { field_type?: string } | undefined
  ): boolean => {
    const current = dynamicFields[key];
    const original = originalFields[key];

    if (field?.field_type === "date") {
      return normalizeDateForCompare(current) !== normalizeDateForCompare(original);
    }
    return String(current ?? "") !== String(original ?? "");
  };

  const saveHandler = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fill all required fields.");
      return;
    }

    const editedFields = Object.keys(dynamicFields).reduce((acc, key) => {
      const field = dynamicData.find((f) => f.label === key);
      if (!hasDynamicFieldChanged(key, field)) {
        return acc;
      }
      if (field?.field_type === "date") {
        const raw = dynamicFields[key];
        if (raw === null || raw === undefined || raw === "") {
          acc[key] = "";
        } else {
          acc[key] = new Date(raw).toISOString().replace("Z", "+00:00");
        }
      } else {
        acc[key] = dynamicFields[key] ?? "";
      }
      return acc;
    }, {});

    const updateWorkflowObj = {
      dynamic_fields: editedFields,
      status_type: selectedStatus,
      status:
        selectedAllStatus?.status_type === "wip" ? selectedAllStatus?.id : null, // WIP
      closed_status:
        selectedAllStatus?.status_type === "closed"
          ? selectedAllStatus?.id
          : null, // closed
    };

    const selectedEmailId = otherMailDetails?.id;
    let queryParam = {
      to: emails,
      html_content: editorHtml,
      bcc: bcc,
      cc: cc,
      attachments: files,
      reply_and_close: true,
      template_attachments: Array.isArray(templateAttachment)
        ? templateAttachment.map((attachment) => attachment.id)
        : [],
      is_replied: true,
    };

    try {
      await saveInternalNote({
        attachments: internalNoteFiles,
        content: internalNoteText.trim(),
        ticket: ticketId,
      }).unwrap();

      const response = await trigger({ id: ticketId, body: updateWorkflowObj });

      if (response.data) {
        const replyResponse = await replyMail({
          body: queryParam,
          selectedEmailId,
        });

        if (replyResponse && replyResponse.data) {
          toast.success("Email sent successfully");
          setCheckingForReplyApi(true);
          refetch();
          closeModal();
        } else if ("data" in replyResponse?.error) {
          const errorData = replyResponse.error?.data as { detail?: string[] };
          const errorMessage = errorData?.detail?.[0];
          toast.error(errorMessage);
        }
      } else if ("data" in response.error) {
        const errorData = response.error?.data as { detail?: string[] };
        const errorMessage = errorData?.detail?.[0];
        toast.error(errorMessage);
      }
      setShowReplyCard(false);
    } catch (error: any) {
      const errorMessage =
        error?.data?.detail?.[0] ||
        error?.data?.detail ||
        "Failed to save workflow. Please try again.";
      toast.error(errorMessage);
      setShowReplyCard(false);
    }
  };

  const getDataTimeFormatted = (date) => {
    if (date && date.length > 0) {
      const dateObj = new Date(date);

      // Manually format the date to ensure no timezone conversion issues
      const year = dateObj.getUTCFullYear();
      const month = String(dateObj.getUTCMonth() + 1).padStart(2, "0"); // Months are zero-based
      const day = String(dateObj.getUTCDate()).padStart(2, "0");
      const hours = String(dateObj.getUTCHours()).padStart(2, "0");
      const minutes = String(dateObj.getUTCMinutes()).padStart(2, "0");

      const formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;
      return formattedDate;
    } else {
      return "";
    }
  };

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<{ value: any }>, key: string) => {
      // Clear error only for the checkbox being changed
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[key];
        return newErrors;
      });
      setIsButtonDisabled(true);
      setDynamicFields((prevFields) => ({
        ...prevFields,
        [key]: event.target.value,
      }));
    },
    []
  );

  const handleInternalNotesChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = event.target.value;
      setInternalNoteText(value);
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors.internal_notes;
        return newErrors;
      });
      setIsButtonDisabled(true);
    },
    []
  );

  const handleDeleteFile = (fileToDelete) => () => {
    setInternalNoteFiles((prevFiles) =>
      prevFiles.filter((file) => file !== fileToDelete)
    );
  };

  const handleFileChange = async (event) => {
    const newFiles = Array.from(event.target.files);
    const filePromises = newFiles.map((file: any) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e: any) => {
          resolve({
            file_name: file.name,
            file: e.target.result.split(",")[1],
          });
        };
        reader.onerror = (error) => reject(error);
        reader.readAsDataURL(file);
      });
    });

    try {
      const fileDataArray = await Promise.all(filePromises);
      setInternalNoteFiles((prevFiles) => [...prevFiles, ...fileDataArray]);
    } catch (error) {
      toast.error("An error occurred while uploading files. Please try again.");
    }
  };

  const formatStatusData = useCallback((status) => {
    if (typeof status.status === "string") {
      // Open and Reopen statuses (no ID)
      return {
        status_type: status.status_type,
        status: status.status,
        id: null, // No ID for statuses without ID
      };
    } else {
      // WIP and Closed statuses (with ID)
      return {
        id: status.status.id,
        status_type: status.status_type,
        status: status.status.status,
      };
    }
  }, []);

  // Updated handler to also update the current status display
  const handlerStatus = (allStatus) => {
    setAllSelectedStatus(allStatus);
    setSelectedStatus(allStatus?.status_type);
    setCurrentStatus(allStatus?.status); // Update the display value
    setIsButtonDisabled(true); // Enable save button when status changes
    // Clear status error when status is selected
    setErrors((prevErrors) => {
      const newErrors = { ...prevErrors };
      delete newErrors.status_type;
      return newErrors;
    });
  };

  // Handle the Select onChange event
  const handleStatusChange = (event) => {
    const selectedStatusValue = event.target.value;
    setCurrentStatus(selectedStatusValue); // Update the display value immediately

    // Find the matching status from the options
    const status = statusOptions?.find(
      (option) => option.status === selectedStatusValue
    );

    if (status) {
      // Update the full status object and type
      handlerStatus(status);
    }
  };
  console.log(fetchDispositionStatus, "fetchDispositionStatus");

  const fetchingTenant = window.location.hostname.split(".")[0];
  const tenant = String(fetchingTenant || "").toLowerCase();
  const shouldHideReopen = tenant === "payswiff" || tenant === "payswiffibl";

  const statusOptions = fetchDispositionStatus?.all_statuses
    ?.filter((status) => !(shouldHideReopen && status?.status_type === "reopen"))
    ?.map((status) => formatStatusData(status));

  const truncateDescription = (description, maxLength) => {
    if (description?.length > maxLength) {
      return description?.slice(0, maxLength) + "...";
    }
    return description;
  };

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 120,
      },
    },
  };

  return (
    <div style={{ maxHeight: "60vh", marginBottom: "10px" }}>
      {dynamicFieldsLoader ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "200px",
          }}
        >
          <LoadingSpinner />
        </div>
      ) : (
        <>
      <div
        style={{
          maxHeight: "50vh",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: "20px",
        }}
      >
        <div
          style={{
            marginTop: "15px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          {dynamicData?.map((field: any) => (
            <React.Fragment key={field?.label}>
              {field.field_type !== "multi-level" ? (
                <div
                  key={field.label}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <InputLabel htmlFor={`dynamic_fields.${field.label}`}>
                    {field.label}
                    {field.is_required && <span className="required"></span>}
                  </InputLabel>
                  {field.field_type === "text" && (
                  <TextField
                    id={`dynamic_fields.${field.label}`}
                    variant="outlined"
                    fullWidth
                    value={dynamicFields[field.label] || ""}
                    onChange={(e) =>
                      handleInputChange(field.label, e.target.value)
                    }
                    required={field.is_required}
                  />
                )}
                {field.field_type === "email" && (
                  <TextField
                    type="email"
                    id={`dynamic_fields.${field.label}`}
                    variant="outlined"
                    fullWidth
                    value={dynamicFields[field.label] || ""}
                    onChange={(e) =>
                      handleInputChange(field.label, e.target.value)
                    }
                    required={field.is_required}
                  />
                )}
                {field.field_type === "number" && (
                  <TextField
                    type="number"
                    id={`dynamic_fields.${field.label}`}
                    variant="outlined"
                    fullWidth
                    value={dynamicFields[field.label] || ""}
                    onChange={(e) =>
                      handleInputChange(field.label, e.target.value)
                    }
                    required={field.is_required}
                  />
                )}
                {field.field_type === "date" && (
                  <TextField
                    type="date"
                    id={`dynamic_fields.${field.label}`}
                    variant="outlined"
                    fullWidth
                    value={dynamicFields[field.label] || ""}
                    onChange={(e) =>
                      handleInputChange(field.label, e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      inputProps: {
                        style: { textTransform: "uppercase" }, // Optional: uppercase input text
                      },
                    }}
                    required={field.is_required}
                  />
                )}
                {field.field_type === "time" && (
                  <TextField
                    type="time"
                    id={`dynamic_fields.${field.label}`}
                    variant="outlined"
                    fullWidth
                    value={dynamicFields[field.label] || ""}
                    onChange={(e) =>
                      handleInputChange(field.label, e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    required={field.is_required}
                  />
                )}
                {field.field_type === "date-time" && (
                  <TextField
                    type="datetime-local"
                    id={`dynamic_fields.${field.label}`}
                    variant="outlined"
                    fullWidth
                    defaultValue={getDataTimeFormatted(
                      dynamicFields[field.label]
                    )}
                    onChange={(e) =>
                      handleInputChange(field.label, e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      inputProps: {
                        style: { textTransform: "uppercase" }, // Optional: uppercase input text
                      },
                    }}
                    required={field.is_required}
                  />
                )}
                {field.field_type === "text-area" && (
                  <TextField
                    id={`dynamic_fields.${field.label}`}
                    variant="outlined"
                    fullWidth
                    multiline
                    value={dynamicFields[field.label] || ""}
                    onChange={(e) =>
                      handleInputChange(field.label, e.target.value)
                    }
                    required={field.is_required}
                  />
                )}
                {field.field_type === "drop-down" && (
                  <FormControl fullWidth>
                    <Select
                      labelId={`select-label-${field.label}`}
                      id={`select-${field.label}`}
                      value={dynamicFields[field.label] || ""}
                      onChange={(e) =>
                        handleInputChange(field.label, e.target.value)
                      }
                      required={field.is_required}
                    >
                      <MenuItem selected disabled>
                        Select
                      </MenuItem>
                      {field?.choices?.map((option: any) => (
                        <MenuItem key={option.id} value={option.choice}>
                          {option.choice}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {field.field_type === "check-box" && (
                  <FormControl fullWidth>
                    <Select
                      labelId={`checkbox-label-${field.label}`}
                      id={`checkbox-${field.label}`}
                      required={field.is_required}
                      multiple
                      value={dynamicFields[field.label] || []} // Ensure value is an array of IDs
                      onChange={(e: any) =>
                        handleCheckboxChange(e, field.label)
                      }
                      renderValue={(selected) => {
                        // Create an array of selected choice labels
                        const selectedLabels = (field?.multiple_choices || [])
                          .filter((choice) =>
                            selected.includes(String(choice.id))
                          )
                          .map((choice) => choice.choice);
                        return selectedLabels.join(", ");
                      }}
                    >
                      {field?.multiple_choices?.map((choice: any) => (
                        <MenuItem key={choice.id} value={String(choice.id)}>
                          <Checkbox
                            checked={dynamicFields[field.label]?.includes(
                              String(choice.id)
                            )}
                          />
                          <ListItemText primary={choice.choice} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {field.field_type === "tel-phone" && (
                  <PhoneInput
                    defaultCountry="IN"
                    value={dynamicFields[field.label] || "+91"}
                    onChange={(value) => handleInputChange(field.label, value)}
                    required={field.is_required}
                  />
                )}
                  {errors[field.label] && (
                    <div className="mt-2" style={{ color: "red" }}>
                      {capitalizeFirstLetter(errors[field.label])}
                    </div>
                  )}
                </div>
              ) : (
                field.field_type === "multi-level" && (
                  <LazyMultiLevelDropdown
                    field={field}
                    value={getMultiLevelDisplayValue(dynamicFields[field.label])}
                    ticketPath={getMultiLevelTicketPath(dynamicFields[field.label])}
                    prefillFromTicketValue
                    onFinalSelect={(lastChoice, fullPath) => {
                      setIsButtonDisabled(true);
                      setDynamicFields((prev) => ({
                        ...prev,
                        [field.label]: fullPath || lastChoice.choice,
                      }));
                      setErrors((prevErrors) => {
                        const newErrors = { ...prevErrors };
                        delete newErrors[field.label];
                        return newErrors;
                      });
                    }}
                    error={
                      errors[field.label] ? String(errors[field.label]) : undefined
                    }
                  />
                )
              )}
            </React.Fragment>
          ))}
        </div>

        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <label>
            Internal Notes <span style={{ color: "red" }}>*</span>
          </label>
          <textarea
            className="form-control form-control-lg form-control-solid mb-2 mt-2"
            value={internalNoteText}
            onChange={handleInternalNotesChange}
            rows={4}
            placeholder="Enter internal notes here"
            required
          />
          {internalNoteFiles.length > 0 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
              {internalNoteFiles.map((file, index) => (
                <Chip
                  key={index}
                  label={file.file_name}
                  onDelete={handleDeleteFile(file)}
                />
              ))}
            </Box>
          )}
          <div className="d-flex justify-content-end">
            <Button
              component="label"
              className={`btn btn-active-light-primary btn-icon ${internalNoteText ? "" : "disabled"}`}
              title="You can add attachments for the following file types: xls,pdf,csv,doc,jpg,png up to a maximum size of 15MB"
            >
              <i className="bi bi-paperclip align-self-center text-dark fs-2 mx-2"></i>
              <input
                type="file"
                hidden
                multiple
                onChange={handleFileChange}
                disabled={!internalNoteText}
              />
            </Button>
          </div>
          {errors.internal_notes && (
            <div className="mt-2" style={{ color: "red" }}>
              {capitalizeFirstLetter(errors.internal_notes)}
            </div>
          )}
        </div>
        <div className="row g-5 g-xl-8 mb-4">
          <div className="col-xl-6" style={{ width: "100%" }}>
            <FormControl className="w-100">
              <InputLabel id="status-select-label" style={{ zIndex: 0 }}>
                Change Status
              </InputLabel>
              <Select
                labelId="status-select-label"
                id="status-select"
                value={currentStatus}
                onChange={handleStatusChange}
                label="Change Status"
                MenuProps={MenuProps}
              >
                {statusOptions?.map((status: any) => (
                  <MenuItem
                    key={status?.id || status?.status}
                    value={status?.status}
                    data-bs-toggle="tooltip"
                    title={status?.status}
                  >
                    {truncateDescription(status?.status, 25)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {errors?.status_type && (
              <div className="text-danger">{errors?.status_type}</div>
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "end",
        }}
      >
        <button
          className="btn btn-primary"
          onClick={saveHandler}
    
        >
          {workFlowLoader || replyLoader || isSavingInternalNote ? (
            <>
              Saving...
              <Loader />
            </>
          ) : (
            "Save"
          )}
        </button>
      </div>
        </>
      )}
    </div>
  );
};

export default WorkflowModal;